import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Phone, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const diff = (new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return Math.round(diff);
};

// ===== Associate-side widget =====
// Shows pending Lead Call Requests in the user's dashboard.
export function PendingCallRequestsWidget() {
  const [rows, setRows] = useState([]);
  const [respondTarget, setRespondTarget] = useState(null);
  const [called, setCalled] = useState(null); // true | false
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/lead-call-requests?scope=mine");
      setRows(data || []);
    } catch { /* silent */ }
  };
  useEffect(() => {
    load();
    // Auto-refresh every 30s, plus immediately when the window regains focus
    // (so logging a CRM report in another tab / page clears this widget fast).
    const t = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);

  const submit = async () => {
    if (called === null) { toast.error("Please pick Yes or No"); return; }
    if (called === true && !remark.trim()) { toast.error("A log remark is required when you mark the call as made"); return; }
    setBusy(true);
    try {
      await api.patch(`/lead-call-requests/${respondTarget.id}`, { called, log_remark: remark.trim() });
      toast.success(called ? "Call logged ✓" : "Deferred — try in next 3 days");
      setRespondTarget(null); setCalled(null); setRemark("");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (rows.length === 0) return null;

  return (
    <div className="pk-depth pk-depth-amber relative overflow-hidden rounded-xl p-5 group" data-testid="dash-pending-call-requests">
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-amber-200/40 rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <Phone size={22} strokeWidth={2} className="text-amber-600 shrink-0 group-hover:scale-110 transition-transform" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-amber-700">Pending Call Requests</div>
            <div className="text-[11.5px] text-amber-700/80 font-semibold">{rows.length} awaiting your response</div>
          </div>
        </div>
        <div className="space-y-2">
          {rows.map((r) => {
            const due = daysUntil(r.due_at);
            const overdue = r.effective_status === "escalated";
            return (
              <div key={r.id} className={`rounded-lg p-3 border bg-white/70 backdrop-blur-sm hover:border-amber-300 transition-colors ${overdue ? "border-rose-300" : "border-amber-100"}`} data-testid={`dash-call-row-${r.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">{r.client_name}</div>
                    <div className="text-[11px] text-slate-600">
                      Asked by <span className="font-semibold">{r.requested_by_name}</span> on {fmtDate(r.requested_at)}
                    </div>
                    {r.note && <div className="text-[11.5px] text-slate-700 mt-1 italic">&ldquo;{r.note}&rdquo;</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    {overdue ? (
                      <Badge className="bg-rose-100 text-rose-700 ring-1 ring-rose-200">Overdue</Badge>
                    ) : due !== null && (
                      <Badge className="bg-amber-100 text-amber-800 ring-1 ring-amber-200">Due in {due >= 0 ? `${due}d` : "now"}</Badge>
                    )}
                    <Button
                      size="sm"
                      className="h-8 text-[11px] bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-sm"
                      onClick={() => { setRespondTarget(r); setCalled(null); setRemark(""); }}
                      data-testid={`dash-call-respond-${r.id}`}
                    >
                      Respond
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!respondTarget} onOpenChange={(o) => !o && setRespondTarget(null)}>
        <DialogContent className="max-w-md" data-testid="dash-call-respond-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Did you call {respondTarget?.client_name}?</DialogTitle>
            <div className="text-[11px] text-slate-500">Requested by {respondTarget?.requested_by_name} · {fmtDate(respondTarget?.requested_at)}</div>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Button
                onClick={() => setCalled(true)}
                className={`flex-1 h-12 ${called === true ? "bg-emerald-600 text-white" : "bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}
                data-testid="dash-call-yes"
              >
                <CheckCircle2 size={14} className="mr-1.5" /> Yes — call made
              </Button>
              <Button
                onClick={() => setCalled(false)}
                className={`flex-1 h-12 ${called === false ? "bg-rose-600 text-white" : "bg-white border border-rose-300 text-rose-700 hover:bg-rose-50"}`}
                data-testid="dash-call-no"
              >
                <AlertTriangle size={14} className="mr-1.5" /> Not yet
              </Button>
            </div>
            {called === true && (
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">Log Remark *</Label>
                <Textarea
                  rows={3}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="Summarise the conversation, next steps, follow-up date…"
                  className="mt-1.5"
                  data-testid="dash-call-remark"
                />
              </div>
            )}
            {called === false && (
              <div className="text-[11.5px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2.5">
                ⏳ Please call this school within 3 days. If you don&apos;t, this will surface on your admin&apos;s dashboard.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespondTarget(null)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || called === null} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="dash-call-submit">
              {busy ? "Saving…" : "Save Response"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== Admin-side widget =====
// Master Admin / CRM Manager / Sales Admin see all overdue call requests so
// they can step in, log the call themselves, and notify the associate.
export function OverdueCallRequestsWidget() {
  const { user } = useAuth();
  const canSee = user?.role === "master_admin" || user?.role === "crm_manager" || user?.role === "sales_admin";
  const [rows, setRows] = useState([]);
  const [target, setTarget] = useState(null);
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!canSee) return;
    try {
      const { data } = await api.get("/lead-call-requests?scope=overdue");
      setRows(data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, [canSee]); // eslint-disable-line

  if (!canSee || rows.length === 0) return null;

  const submit = async () => {
    if (!remark.trim()) { toast.error("A log remark is required"); return; }
    setBusy(true);
    try {
      await api.post(`/lead-call-requests/${target.id}/admin-resolve`, { log_remark: remark.trim() });
      toast.success("Call logged on behalf of associate ✓");
      setTarget(null); setRemark("");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="pk-depth pk-depth-rose relative overflow-hidden rounded-xl p-5 group" data-testid="dash-overdue-call-requests">
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-rose-200/40 rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={22} strokeWidth={2} className="text-rose-600 shrink-0 group-hover:scale-110 transition-transform" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-rose-700">Overdue Call Requests</div>
            <div className="text-[11.5px] text-rose-700/80 font-semibold italic">{rows.length} not followed up within 3 days</div>
          </div>
        </div>
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-lg p-3 border border-rose-100 bg-white/70 backdrop-blur-sm hover:border-rose-300 transition-colors" data-testid={`dash-overdue-row-${r.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-semibold text-slate-900 text-sm">{r.client_name}</div>
                  <div className="text-[11px] text-slate-600">
                    Requested by <span className="font-semibold">{r.requested_by_name}</span> →
                    assigned to <span className="font-semibold text-rose-700">{r.assigned_to_name}</span> on {fmtDate(r.requested_at)}
                  </div>
                  {r.note && <div className="text-[11.5px] text-slate-700 mt-1 italic">&ldquo;{r.note}&rdquo;</div>}
                </div>
                <Button
                  size="sm"
                  className="h-8 text-[11px] bg-gradient-to-br from-rose-600 to-pink-700 hover:from-rose-700 hover:to-pink-800 text-white shadow-sm"
                  onClick={() => { setTarget(r); setRemark(""); }}
                  data-testid={`dash-overdue-resolve-${r.id}`}
                >
                  Mark Call Made
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="max-w-md" data-testid="dash-overdue-resolve-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Mark Call Made — {target?.client_name}</DialogTitle>
            <div className="text-[11px] text-slate-500">
              You&apos;re logging this call on behalf of <span className="font-semibold">{target?.assigned_to_name}</span>. They will be notified.
            </div>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Log Remark *</Label>
            <Textarea
              rows={4}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="What was discussed? Next steps?"
              data-testid="dash-overdue-remark"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="dash-overdue-submit">
              {busy ? "Saving…" : "Log Call & Resolve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
